// Good/bad status of one phone number, from public.sms_number_health
// (sql/migrations/2026-09-22-sms-number-health.sql). That table has one row
// per (clinic schema, number); this folds them into one status the same way
// get_bad_number_info (backend/twilio_service/utils.py) does, so a number
// shown as "Bad number" is exactly one the send paths block.

import { supabasePublic } from "../components/supabaseClient";
import { smsE164 } from "./badNumbers";
import { onFetchResponse } from "./fetchTelemetry";
import { getBackendURL } from "../components/env";

export const NUMBER_STATUSES = {
  good: { label: "Good number", short: "Good", letter: "G", colorScheme: "green", hint: "Texts to this number are being delivered" },
  bad: { label: "Bad number", short: "Bad", letter: "B", colorScheme: "red", hint: "Texts fail permanently — correct the number on file" },
  failing: { label: "Failed text", short: "Failed", letter: "F", colorScheme: "yellow", hint: "Recent texts failed, but not because of the number" },
  opt_out: { label: "Opted out", short: "Opted out", letter: "O", colorScheme: "purple", hint: "Patient opted out — do not text" },
  unknown: { label: "No result yet", short: "Pending", letter: "P", colorScheme: "gray", hint: "Texted, but no delivery result yet" },
  untexted: { label: "Not texted yet", short: "Not texted", letter: "U", colorScheme: "gray", hint: "No texts sent to this number yet" },
};

const ts = (v) => (v ? new Date(v).getTime() : 0);

/** One status key (see NUMBER_STATUSES) from all of a number's health rows. */
export const numberStatusFromRows = (rows) => {
  if (!rows || !rows.length) return "untexted";
  if (rows.some((r) => r.verdict === "opt_out")) return "opt_out";
  // A delivery anywhere after a failure proves the number works.
  const lastSuccess = Math.max(0, ...rows.map((r) => ts(r.last_success_at)));
  const failing = rows.filter((r) => r.verdict === "bad" && ts(r.last_failure_at) > lastSuccess);
  if (failing.some((r) => r.error_severity === "permanent")) return "bad";
  if (failing.length) return "failing";
  if (rows.some((r) => r.verdict === "good")) return "good";
  return "unknown";
};

const CACHE_MS = 60 * 1000;
const BATCH_MS = 25; // gather every badge on a page into one request
const CHUNK = 100; // keeps the `phone_number=in.(...)` URL short
const COLUMNS =
  "phone_number, schema_name, verdict, error_severity, last_error_code, error_description, " +
  "delivered_count, failed_count, last_success_at, last_failure_at";

const cache = new Map(); // e164 -> { at, promise }
let pending = new Map(); // e164 -> [resolve]
let timer = null;

const flush = async () => {
  timer = null;
  const batch = pending;
  pending = new Map();
  const numbers = [...batch.keys()];
  let rows = [];
  try {
    for (let i = 0; i < numbers.length; i += CHUNK) {
      const { data, error } = await supabasePublic()
        .from("sms_number_health")
        .select(COLUMNS)
        .in("phone_number", numbers.slice(i, i + CHUNK));
      if (error) throw error;
      rows.push(...(data || []));
    }
  } catch (err) {
    console.warn("[numberStatus] lookup failed:", err);
    rows = null;
  }
  batch.forEach((resolvers, e164) => {
    let result = { status: null, rows: [] };
    if (rows) {
      const mine = rows.filter((r) => r.phone_number === e164);
      result = { status: numberStatusFromRows(mine), rows: mine };
    } else {
      cache.delete(e164); // let the next render retry
    }
    resolvers.forEach((resolve) => resolve(result));
  });
};

/**
 * { status, rows } for a phone number. Rows are the number's sms_number_health
 * rows (every clinic). Lookups made within a few ms of each other — a whole
 * table of badges — share one request, and results are cached for a minute.
 * Resolves { status: null } when it cannot tell (table missing, lookup
 * failed) so callers simply show nothing.
 */
export const loadNumberStatus = (phone) => {
  const e164 = smsE164(phone);
  if (!e164 || e164.length < 11) return Promise.resolve({ status: null, rows: [] });
  const hit = cache.get(e164);
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.promise;
  const promise = new Promise((resolve) => {
    if (!pending.has(e164)) pending.set(e164, []);
    pending.get(e164).push(resolve);
    if (!timer) timer = setTimeout(flush, BATCH_MS);
  });
  cache.set(e164, { at: Date.now(), promise });
  return promise;
};

const changeListeners = new Set();

/** Call fn whenever cached statuses are dropped. Returns an unsubscribe. */
export const onNumberStatusChange = (fn) => {
  changeListeners.add(fn);
  return () => changeListeners.delete(fn);
};

/** Forget every cached status and have each badge on screen read its number again. */
export const refreshNumberStatuses = () => {
  cache.clear();
  changeListeners.forEach((fn) => {
    try {
      fn();
    } catch (_) {
      /* one badge never stops the others */
    }
  });
};

/**
 * Have the backend rebuild sms_number_health for `schemas` now (the SMS Status
 * modal's "Refresh DB" button), then re-read every badge. Resolves the
 * backend's { results: [{ schema, numbers, error }], numbers }. Throws when
 * no schema could be rebuilt.
 */
export const rebuildNumberHealth = async (schemas) => {
  const res = await fetch(`${getBackendURL()}/twilio/refresh-number-health/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ schemas }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const why = (data.results || []).map((r) => `${r.schema}: ${r.error}`).join("; ");
    throw new Error(why || data.error || `HTTP ${res.status}`);
  }
  refreshNumberStatuses();
  return data;
};

// After a text goes out the backend rebuilds sms_number_health (about 2s), and
// again when Twilio's delivery callback lands seconds later. Re-read at a few
// points across that window. A checkpoint still pending is left alone, so a
// batch send refreshes as it goes rather than only once it ends.
const AFTER_SEND_MS = [3000, 10000, 25000, 60000];
const afterSendTimers = AFTER_SEND_MS.map(() => null);

export const refreshAfterSend = () => {
  AFTER_SEND_MS.forEach((ms, i) => {
    if (afterSendTimers[i]) return;
    afterSendTimers[i] = setTimeout(() => {
      afterSendTimers[i] = null;
      refreshNumberStatuses();
    }, ms);
  });
};

// Every SMS send in the app is a POST to a /twilio/...send... or /resend endpoint.
export const SMS_SEND_URL = /\/twilio\/[^?#]*send/;

onFetchResponse((url, method, resp) => {
  if (method === "POST" && resp.ok && SMS_SEND_URL.test(url)) refreshAfterSend();
});
